
import { Component, Prop, Watch, Mixins } from 'vue-property-decorator'
import { Transport } from '@/models/Transport'
import Testable from '@/mixins/Testable'
import axios from 'axios'
import { Invoice } from '@/models/accounting/Invoice'
import moment from 'moment'
import { createModelFromResponse } from '@/utils/model'
import { ValidationProvider } from 'vee-validate'
import FileTag from '@/enums/FileTag'
import { Company } from '@/models/Company'
import { Document } from '@/models/Document'
import { PluralResponse } from 'coloquent/dist/response/PluralResponse'
import { namespace } from 'vuex-class'
import FinancialConnection from '@/models/FinancialConnection'

const Auth = namespace('auth')

@Component({
	components: {
		ValidationProvider
	}
})
export default class InvoiceGeneratorDialog extends Mixins(Testable) {
	@Prop({ required: false, default: null })
	selectedInvoice!: Invoice | null

	@Prop({ required: false, default: () => [] })
	invoices!: Array<Invoice>

	@Prop({ required: false, default: false })
	opened!: boolean

	@Prop({ required: false, default: false })
	generating!: boolean

	@Auth.Getter('firstDayOfWeek')
	firstDayOfWeek!: string

	@Auth.State('financialConnection')
	financialConnection!: FinancialConnection | null

	isLoading: boolean = false
	initialized: boolean = false
	initializing: boolean = false
	fetchNewInvoiceNumber: boolean = false
	fetchingInvoiceNumber: boolean = false
	combinedAttachments: boolean = true

	invoiceNumber: number | null = null
	invoiceNumberPrefix: string | null = null
	invoiceDate: string | null = null
	datePickerMenu: boolean = false
	minDate: string = moment().subtract(12, 'days').format('YYYY-MM-DD')

	selectedDocuments: Array<Document> | null = null
	allDocuments: Array<Document> | null = null
	transports: Array<Transport> | null = null
	selectedLanguage: string | 'nl' = 'nl'
	defaultLanguage: string | 'nl' = 'nl'
	countryName: string | null = null
	locales: Array<string> = ['nl', 'en', 'de', 'fr']
	customer: Company | null = null

	async created() {
		this.transports = this.selectedInvoice?.getTransports() ?? null
		await this.initialize()
		this.defineSelectedDocuments()
		this.invoiceDate = moment().format('YYYY-MM-DD')

		if (this.selectedInvoice) {
			if (!this.hasInvoiceNumber) {
				await this.fetchNextInvoiceNumber()
			} else {
				// @ts-ignore
				this.invoiceNumber = this.selectedInvoice.fullInvoiceNumber
			}
		}

		if (this.selectedInvoice?.getCustomer()) {
			this.customer = (await Company.find(this.selectedInvoice!.getCustomer()!.getApiId()!)).getData() as Company
			this.countryName = this.customer.getPrimaryPhoneCountryCode()?.countryName ?? null
			this.defaultLanguage = this.locales.find((l) => l === this.countryName?.toLowerCase()) ?? 'nl'

			const localStorageKey = 'invoice_lang_' + this.customer.getApiId()
			this.selectedLanguage = localStorage.getItem(localStorageKey)
				? JSON.parse(localStorage.getItem(localStorageKey) ?? '{}')
				: this.defaultLanguage
		}

		this.combinedAttachments = this.isHistoricallyCombiningAttachments()
	}

	@Watch('opened')
	async onOpenedChange(isOpened) {
		if (!this.initializing && isOpened) {
			await this.initialize()
		}
	}

	defineSelectedDocuments() {
		const buildingMaterialDocuments = this.allDocuments
			?.filter((d) => d.tag === FileTag.BUILDING_MATERIALS)
			.filter((d) => d.canBeAttached)
		const unloadingDocuments = this.allDocuments
			?.filter((d) => d.tag === FileTag.UNLOADING_DOCUMENT)
			.filter((d) => d.canBeAttached)
		if (buildingMaterialDocuments?.length === 1 && unloadingDocuments?.length === 0) {
			// @ts-ignore
			this.selectedDocuments = [...buildingMaterialDocuments]
		} else if (unloadingDocuments?.length === 1 && buildingMaterialDocuments?.length === 0) {
			// @ts-ignore
			this.selectedDocuments = [...unloadingDocuments]
		} else {
			this.selectedDocuments = null
		}

		return this.selectedDocuments
	}

	async initialize() {
		this.initializing = true

		if (this.transports?.length) {
			await this.fetchAllDocuments()
		}

		this.initialized = true
		this.initializing = false
	}

	async fetchAllDocuments() {
		const transportIds = this.transports?.map((t) => t.getApiId())
		let query = Document.query().with('transport')

		transportIds?.forEach((id) => (query = query.option('filter[transports][]', id as string)))
		this.allDocuments = ((await query.get()) as PluralResponse).getData() as Array<Document>
	}

	async handleGenerateClick() {
		const valid = await this.validate()
		if (!valid) return
		this.rememberCombiningAttachments(this.combinedAttachments)

		this.$emit('generate', {
			attachedDocuments: this.selectedDocuments,
			invoiceNumber: this.invoiceNumber,
			invoiceDate: this.invoiceDate,
			hasInvoiceNumber: this.hasInvoiceNumber,
			selectedLanguage: this.selectedLanguage,
			combinedAttachments: this.combinedAttachments
		})

		if (this.customer) {
			window.localStorage.setItem(
				'invoice_lang_' + this.customer?.getApiId(),
				JSON.stringify(this.selectedLanguage)
			)
		}
	}

	isHistoricallyCombiningAttachments(): boolean {
		const storedChoices = localStorage.getItem('invoicing_combine_attachments_last_choices')

		if (!storedChoices) return true
		return JSON.parse(storedChoices).filter(Boolean).length > 1
	}

	rememberCombiningAttachments(combinedAttachments: boolean): void {
		const previousChoices = this.getPreviousChoices()
		const newLastChoices = [...previousChoices, combinedAttachments].slice(-3)
		localStorage.setItem('invoicing_combine_attachments_last_choices', JSON.stringify(newLastChoices))
	}

	private getPreviousChoices(): Array<boolean> {
		const storedChoices = localStorage.getItem('invoicing_combine_attachments_last_choices')
		let previousChoices: Array<boolean> = []
		if (storedChoices) {
			previousChoices = JSON.parse(storedChoices)
		}
		return previousChoices
	}

	async validate() {
		let invoiceNumberValid = true
		if (this.$refs.invoiceNumber) {
			// @ts-ignore
			invoiceNumberValid = (await (this.$refs.invoiceNumber as ValidationProvider).validate()).valid
		}
		return invoiceNumberValid
	}

	get hasInvoiceNumber() {
		return !!this.selectedInvoice?.invoiceNumber
	}

	get hasVATNumber() {
		return !!this.selectedInvoice?.getCustomer()?.vatNumber
	}

	get showDocumentSelectors() {
		return !!this.transports?.map((t) => t.getDocuments())
	}

	@Watch('invoices')
	checkInvoiceNumberConflicts(invoices) {
		if (invoices.find((i) => i.invoiceNumber === this.invoiceNumber) && !this.hasInvoiceNumber) {
			this.fetchNewInvoiceNumber = true
		}
	}

	async fetchNextInvoiceNumber() {
		if (this.fetchingInvoiceNumber) return
		this.fetchingInvoiceNumber = true
		try {
			const response = await axios.get(`${this.selectedInvoice?.getModelUrl()}/nextInvoiceNumber`)
			const invoice = createModelFromResponse(Invoice, response.data) as Invoice
			this.invoiceNumber = invoice.invoiceNumber
			this.invoiceNumberPrefix = invoice.invoicePrefix
		} finally {
			this.fetchNewInvoiceNumber = false
			this.fetchingInvoiceNumber = false
		}
	}

	documentsForTransport(transport: Transport) {
		if (this.transports?.length) {
			if (this.transports.length === 1) {
				return this.allDocuments
			} else {
				return this.allDocuments?.filter((d) => d.getTransport().getApiId() === transport.getApiId())
			}
		}
	}

	get invoiceNumberInUse() {
		return this.invoices.some((i) => i.fullInvoiceNumber === this.invoiceNumberPrefix + '-' + this.invoiceNumber)
	}

	get fullInvoiceNumberPrefix() {
		if (!this.invoiceDate) return ''
		const includingNumber = [
			this.invoiceNumberPrefix ? `${this.invoiceNumberPrefix}-` : null,
			this.financialConnection?.prefixYear ? moment(this.invoiceDate).format('YY') : null,
			this.financialConnection?.prefixMonth ? moment(this.invoiceDate).format('MM') : null,
			String(this.invoiceNumber ?? 0).padStart(this.financialConnection?.prefixLength ?? 0, '0')
		]
			.filter(Boolean)
			.join('')
		return includingNumber.slice(0, includingNumber.length - String(this.invoiceNumber ?? 0).length)
	}
}
